import { useLocation, useNavigate } from 'react-router-dom'
import { NavBar } from '@versports/ui'
import { useAuth } from '../auth/AuthContext'
import { canSeeAdminShell } from '../auth/adminAccess'
import { isClubless } from '../auth/postLoginRoute'
import { openVrApp } from '../auth/vrBridge'

/**
 * Web-app binding for the shared `NavBar`. Wires the router-agnostic
 * props to react-router (`useLocation` / `useNavigate`) and to our auth
 * stack. El tab **VR** (prop `onTrain`, nombre conservado por el call
 * site del vr-app) hace el puente cookie → bearer y abre el lobby del
 * vr-app; los gates (consentimiento → cuestionarios → sensores) corren
 * adentro del vr-app después de "Comenzar" (mg 2026-09-07, doc 32 §4).
 *
 * `showAdmin` gates on `canSeeAdminShell` (business admin OR devices-only
 * `device_admin`), not the narrower `canSeeAdmin` — a systems-only
 * admin can't see the business tabs, but `/admin` itself already bounces
 * them straight to `/admin/devices` (see AdminLayout). Gating this on
 * `canSeeAdmin` would leave that role with no way to reach Devices at all
 * now that Home's own "Devices" card was removed as a duplicate of this bar.
 *
 * `showTablet` (item **Tablet** → `/tablet`, el modo kiosco del dispositivo
 * del kit) va gateado a staff de club o admin: la ruta en sí es pública —su
 * credencial es el código del kit más el PIN del jugador— y hasta ahora sólo
 * se llegaba tipeando la URL.
 */
export function AppNavBar() {
  const { user } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  const isAdmin = canSeeAdminShell(user)
  const isStaff = !!user?.club_memberships?.length
  // `/tablet` es el kiosco del dispositivo compartido del kit, no una
  // superficie personal: la ruta es pública (la credencial es el código del
  // kit + el PIN del jugador, ver `tablet/TabletLayout.tsx`), así que el item
  // es lo único que decide quién la ve. Se muestra con el mismo criterio que
  // Club/Admin — staff del club o admin —: a un jugador el link sólo le
  // ofrecería una pantalla que le cierra su propia sesión al terminar el turno.
  const showTablet = isAdmin || isStaff
  if (!user && location.pathname !== '/guia') return null
  // Sin club no hay a dónde navegar: `/sin-club` es la única pantalla, y una
  // barra con VR/Data que rebota a la misma pantalla es peor que no
  // tenerla (mg, 2026-09-09 — "no debería poder hacer nada").
  if (user && isClubless(user)) return null

  // El `NavBar` compartido espera la promesa y muestra el estado busy /
  // el error como toast, así que el fallo se propaga a propósito.
  async function onTrain() {
    if (!user) {
      navigate('/login?next=/')
      return
    }
    await openVrApp()
  }

  return (
    <NavBar
      currentPath={location.pathname}
      onNavigate={(href) => navigate(href)}
      onTrain={onTrain}
      showAdmin={isAdmin}
      showClub={isStaff}
      showTablet={showTablet}
      accent={user?.branding_club?.primary_color ?? undefined}
      clubLogoUrl={user?.branding_club?.logo_url ?? undefined}
    />
  )
}
